import React from 'react';
import { FormData } from '../App';
import { districts } from '../data/districts';

interface Props {
  formData: FormData;
  updateFormData: (data: Partial<FormData>) => void;
}

function StateSelection({ formData, updateFormData }: Props) {
  const allStates = React.useMemo(() => {
    return Object.keys(districts).sort();
  }, []);

  const selectedDistricts = formData.state ? districts[formData.state] || [] : [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">Location & Cropping Pattern</h2>
        <p className="text-gray-600 mb-6">
          Select your state, district and preferred rice cropping pattern to get started.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select State <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.state}
            onChange={(e) => {
              updateFormData({ 
                state: e.target.value,
                district: '' // Reset district when state changes
              });
            }}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            required
          >
            <option value="">Select a state</option>
            {allStates.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
          {!formData.state && (
            <p className="mt-1 text-sm text-red-500">Please select a state</p>
          )}
        </div>

        {formData.state && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select District <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.district || ''}
              onChange={(e) => updateFormData({ district: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              required
            >
              <option value="">Select a district</option>
              {selectedDistricts.map((district) => (
                <option key={district} value={district}>
                  {district}
                </option>
              ))}
            </select>
            {!formData.district && (
              <p className="mt-1 text-sm text-red-500">Please select a district</p>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cropping Pattern <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => updateFormData({ croppingPattern: 'TPR' })}
              className={`p-4 border rounded-lg text-center ${
                formData.croppingPattern === 'TPR'
                  ? 'bg-green-50 border-green-500 text-green-700'
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="font-semibold">TPR</div>
              <div className="text-sm text-gray-600">Transplanted Paddy Rice</div>
            </button>
            <button
              type="button"
              onClick={() => updateFormData({ croppingPattern: 'DSR' })}
              className={`p-4 border rounded-lg text-center ${
                formData.croppingPattern === 'DSR'
                  ? 'bg-green-50 border-green-500 text-green-700'
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="font-semibold">DSR</div>
              <div className="text-sm text-gray-600">Direct Seeded Rice</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StateSelection;